import React from 'react'
import { Row, Col, Button, Collapse, } from 'reactstrap'
import PropTypes from 'prop-types'
import { arrayIsNullOrEmpty, filterAddressesByCategory, } from '../../util/helpers'
import ErrorBoundary from '../ErrorBoundary'
import ProgramDetailMap from '../Maps/ProgramDetailMap'

/**
 * Add an index key to each address
 * @param {Array} addresses Addresses list to enhance
 */
const addIndices = (addresses) => {
  if (arrayIsNullOrEmpty(addresses)) {
    return []
  }
  return addresses.reduce((acc, curr, idx) => {
    if (curr) {
      acc.push({ ...curr, index: idx, })
    }
    return acc
  }, [])
}

const noop = () => {}

const AddressSection = ({
  addresses = [],
  heading = '',
  onAddressClick = noop,
}) => {
  const activateAddressIdx = React.useCallback((idx) => {
    onAddressClick(idx)
  }, [ onAddressClick, ])
  const [ collapsed, setCollapsed, ] = React.useState(false)
  const toggleCollapse = React.useCallback(() => {
    if (collapsed) {
      setCollapsed(false)
    } else {
      setCollapsed(true)
    }
  }, [ collapsed, ])
  
  if (!addresses || addresses.length === 0 || !heading){
    return null
  }
  const buildAddresses = () => {
    if (addresses.length > 5) {
      const firstFive = addresses.slice(0, 5)
      const restOfArray = addresses.slice(5)
      return <>
        <ul>
          {
            firstFive.map((a, idx) =>
              <li key={`add-${idx}`}>
                <p className={'body-text-bold heading-5 mb-0'}>
                  <Button color={'link'} className={'address-link-button'} key={`addr-${idx}`} onClick={() => activateAddressIdx(a.index)}>{a.name || a.address}</Button>
                </p>
              </li>
            )
          }
        </ul>
        <Button onClick={toggleCollapse}>Show More</Button>
        <Collapse isOpen={collapsed}>
          <ul>
            {
              restOfArray.map((a, idx) =>
                <li key={`add-${idx}`}>
                  <p className={'body-text-bold heading-5 mb-0'}>
                    <Button color={'link'} className={'address-link-button'} key={`addr-${idx}`} onClick={() => activateAddressIdx(a.index)}>{a.name || a.address}</Button>
                  </p>
                </li>
              )
            }
          </ul>
        </Collapse>
      </>
    }
    else {
      return <ul>
        {
          addresses.map((a, idx) =>
            <li key={`add-${idx}`}>
              <p className={'body-text-bold heading-5 mb-0'}>
                <Button color={'link'} className={'address-link-button'} key={`addr-${idx}`} onClick={() => activateAddressIdx(a.index)}>{a.name || a.address}</Button>
              </p>
            </li>
          )
        }
      </ul>
    }
  }
  return <Row>
    <Col className={''} xs={12}>
      <h4 className={'heading-4'}>{heading}</h4>
      {buildAddresses()}
    </Col>
  </Row>
}

AddressSection.propTypes = {
  addresses      : PropTypes.array,
  heading        : PropTypes.string,
  onAddressClick : PropTypes.func,
}

const DetailLocationSection = ({
  mapLibReady = false,
  program = null,
  userLocation = null,
  subscribeMapChanges = noop,
  programName = '',
  programType = '',
  occupationName = '',
}) => {
  const [ activeAddressIndex, setActiveAddressIndex, ] = React.useState(null)
  const addresses = program ? addIndices(program.addresses) : null
  
  const worksite_addresses = React.useMemo(() => filterAddressesByCategory(addresses, 'worksite'), [ addresses, ])
  const institution_addresses = React.useMemo(() => filterAddressesByCategory(addresses, 'institution'), [ addresses, ])
  const contact_addresses = React.useMemo(() => filterAddressesByCategory(addresses, [ 'contact', 'how_to_apply', ]), [ addresses, ])
  const partner_addresses = React.useMemo(() => filterAddressesByCategory(addresses, 'partner'), [ addresses, ])
  const session_addresses = React.useMemo(() => filterAddressesByCategory(addresses, 'session'), [ addresses, ])
  
  const counties = program ? program.counties : null

  if (!program || (!counties && !addresses)) {
    return null
  }
  return <>
    <Row>
      <Col xs={12}>
        <ErrorBoundary>
        &nbsp;
          {mapLibReady && <ProgramDetailMap
            addresses={addresses}
            containerStyle={{ height: '600px', width: '100%', }}
            subscribeChanges={subscribeMapChanges}
            activeAddressIndex={activeAddressIndex}
            setActiveAddressIndex={setActiveAddressIndex}
            userLocation={userLocation}
            mapLibReady={mapLibReady}
            programName={programName}
            programType={programType}
            occupationName={occupationName}
          />}
        </ErrorBoundary>
      </Col>
    </Row>
    <Row>
      <Col xs={12}>
        <h3 className={'heading-3 mt-5 mt-md-3 mb-4'}>Locations</h3>
      </Col>
    </Row>
    {
      counties && counties.length > 0 &&
        <Row className={'mb-4'}>
          <Col xs={12}>
            <h3 className={'heading-4'}>Operating in the Following Counties</h3>
          </Col>
          <Col xs={12}>
            <span>{counties}</span>
          </Col>
        </Row>
    }
    { 
      !arrayIsNullOrEmpty(contact_addresses) && 
        <AddressSection 
          addresses={contact_addresses} 
          heading={'Contacts'}
          onAddressClick={setActiveAddressIndex}
        /> 
    }
    { 
      !arrayIsNullOrEmpty(institution_addresses) && 
        <AddressSection 
          addresses={institution_addresses} 
          heading={'Institutions'} 
          onAddressClick={setActiveAddressIndex}
        /> 
    }
    { 
      !arrayIsNullOrEmpty(partner_addresses) && 
        <AddressSection 
          addresses={partner_addresses} 
          heading={'Partners'} 
          onAddressClick={setActiveAddressIndex}
        /> 
    }
    { 
      !arrayIsNullOrEmpty(worksite_addresses) && 
        <AddressSection 
          addresses={worksite_addresses} 
          heading={'Worksites'} 
          onAddressClick={setActiveAddressIndex}
        /> 
    }
    {
      !arrayIsNullOrEmpty(session_addresses) && 
        <AddressSection 
          addresses={session_addresses} 
          heading={'Sessions'} 
          onAddressClick={setActiveAddressIndex}
        /> 
    }
  </>
}

DetailLocationSection.propTypes = {
  mapLibReady         : PropTypes.bool,
  program             : PropTypes.object,
  subscribeMapChanges : PropTypes.func,
  programName         : PropTypes.string,
  programType         : PropTypes.string,
  occupationName      : PropTypes.string,
  userLocation        : PropTypes.object,
}

export default DetailLocationSection